// The route table of Milestone C (docs/architecture/milestone-c-contracts.md section 4). FROZEN at the Wave 0 commit: the
// paths, methods, owners and body schemas below do not change without a reviewed edit of the contract document. Lanes register
// the routes they own (one `register*Routes` line each in apps/api/src/app.ts) and the clients call these paths.
//
// `idempotencyKey`: `required` means the `Idempotency-Key` header is required and a replay returns the stored outcome.
// `authorization`: who may call. Staff routes also need an ACTIVE staff user, an unrevoked grant and aal2 outside development.
// `routeClass`: the class of `apps/api/src/policies.ts` that the route book of the app gives the path (`route-book.ts`: the audience prefix, or the class that the route declares; rate limit, body size, timeout).
// Schema names refer to exports of `@lafayette/schemas`. `existing` routes were built in Phase 2.6 or 5 and are reused.
const CUSTOMER_ERRORS = [
    "401 unauthenticated",
    "403 forbidden",
    "429 rate_limited",
    "503 service_unavailable",
];
const STAFF_ERRORS = [
    "401 unauthenticated",
    "403 forbidden",
    "429 rate_limited",
    "503 service_unavailable",
];
export const MILESTONE_C_ROUTES = Object.freeze({
    // --- lane A: messaging --------------------------------------------------------------------------------------------------
    customerMessagingSession: {
        id: "MC-A1",
        lane: "A",
        method: "POST",
        path: "/customer/messaging/session",
        routeClass: "customer",
        authorization: "customer",
        request: null,
        response: "customerMessagingSessionSchema",
        success: 200,
        idempotencyKey: "none",
        existing: false,
        errors: [...CUSTOMER_ERRORS, "503 service_unavailable (messaging off or Stream unavailable)"],
    },
    staffMessagingSession: {
        id: "MC-A2",
        lane: "A",
        method: "POST",
        path: "/staff/messaging/session",
        routeClass: "staff",
        authorization: "concierge|supervisor",
        request: null,
        response: "staffMessagingSessionSchema",
        success: 200,
        idempotencyKey: "none",
        existing: false,
        errors: [...STAFF_ERRORS],
    },
    streamWebhook: {
        id: "MC-A3",
        lane: "A",
        method: "POST",
        path: "/webhooks/stream",
        routeClass: "webhooks",
        authorization: "stream-webhook",
        request: "streamWebhookEventSchema (raw body, X-Signature checked first)",
        response: '{ "ok": true }',
        success: 200,
        idempotencyKey: "none",
        existing: false,
        errors: [
            "400 invalid_request (body not JSON or not an event; final for Stream)",
            "401 unauthenticated (signature missing or wrong; final for Stream)",
            "413 payload_too_large",
            "503 service_unavailable (database; Stream retries)",
        ],
    },
    // --- lane B: card hydration ---------------------------------------------------------------------------------------------
    customerCatalogItemsBatch: {
        id: "MC-B1",
        lane: "B",
        method: "GET",
        path: "/customer/catalog/items?ids=<uuid>,<uuid>",
        routeClass: "customer",
        authorization: "customer",
        request: "ids: 1 to 12 uuids",
        response: "{ items: customerCatalogItemViewSchema[], unavailableIds: uuid[] }",
        success: 200,
        idempotencyKey: "none",
        existing: false,
        errors: [...CUSTOMER_ERRORS, "400 invalid_request"],
    },
    // --- lane C: handoff ----------------------------------------------------------------------------------------------------
    customerRequestHandoff: {
        id: "MC-C0",
        lane: "C",
        method: "POST",
        path: "/customer/conversation/handoff",
        routeClass: "customer",
        authorization: "customer",
        request: "existing handoff body (reasonCode, reasonSummary)",
        response: "customerConversationViewSchema",
        success: 200,
        idempotencyKey: "required",
        existing: true,
        errors: [...CUSTOMER_ERRORS, "409 conflict (handoff.already_open)"],
    },
    staffHandoffAccept: {
        id: "MC-C1",
        lane: "C",
        method: "POST",
        path: "/staff/handoffs/:handoffId/accept",
        routeClass: "staff",
        authorization: "concierge|supervisor",
        request: "staffHandoffAcceptBodySchema",
        response: "staffHandoffCommandResultSchema",
        success: 200,
        idempotencyKey: "required",
        existing: true,
        errors: [...STAFF_ERRORS, "404 not_found", "409 conflict + handoffConflict"],
    },
    staffHandoffReassign: {
        id: "MC-C2",
        lane: "C",
        method: "POST",
        path: "/staff/handoffs/:handoffId/reassign",
        routeClass: "staff",
        authorization: "concierge|supervisor",
        request: "staffHandoffReassignBodySchema",
        response: "staffHandoffCommandResultSchema",
        success: 200,
        idempotencyKey: "required",
        existing: false,
        errors: [...STAFF_ERRORS, "404 not_found", "409 conflict + handoffConflict"],
    },
    staffHandoffRelease: {
        id: "MC-C3",
        lane: "C",
        method: "POST",
        path: "/staff/handoffs/:handoffId/release",
        routeClass: "staff",
        authorization: "concierge|supervisor",
        request: "staffHandoffReleaseBodySchema",
        response: "staffHandoffCommandResultSchema",
        success: 200,
        idempotencyKey: "required",
        existing: true,
        errors: [...STAFF_ERRORS, "404 not_found", "409 conflict + handoffConflict"],
    },
    staffHandoffCancel: {
        id: "MC-C4",
        lane: "C",
        method: "POST",
        path: "/staff/handoffs/:handoffId/cancel",
        routeClass: "staff",
        authorization: "supervisor",
        request: "staffHandoffCancelBodySchema",
        response: "staffHandoffCommandResultSchema",
        success: 200,
        idempotencyKey: "required",
        existing: false,
        errors: [...STAFF_ERRORS, "404 not_found", "409 conflict + handoffConflict"],
    },
    staffHandoffEscalate: {
        id: "MC-C5",
        lane: "C",
        method: "POST",
        path: "/staff/handoffs/:handoffId/escalate",
        routeClass: "staff",
        authorization: "concierge|supervisor",
        request: "staffHandoffEscalateBodySchema",
        response: "staffHandoffCommandResultSchema",
        success: 200,
        idempotencyKey: "required",
        existing: false,
        errors: [...STAFF_ERRORS, "404 not_found", "409 conflict + handoffConflict"],
    },
    staffInbox: {
        id: "MC-C6",
        lane: "C",
        method: "GET",
        path: "/staff/inbox?filter=&cursor=&limit=",
        routeClass: "staff",
        authorization: "concierge|supervisor",
        request: "staffInboxQuerySchema",
        response: "staffInboxPageSchema",
        success: 200,
        idempotencyKey: "none",
        existing: false,
        errors: [...STAFF_ERRORS, "400 invalid_request"],
    },
    // --- lane E: staff workspace and copilot --------------------------------------------------------------------------------
    staffConversationContext: {
        id: "MC-E1",
        lane: "E",
        method: "GET",
        path: "/staff/conversations/:conversationId/context",
        routeClass: "staff",
        authorization: "concierge|supervisor",
        request: null,
        response: "staffConversationContextSchema (lane E, composes existing staff views)",
        success: 200,
        idempotencyKey: "none",
        existing: false,
        errors: [...STAFF_ERRORS, "404 not_found"],
    },
    staffConversationJoin: {
        id: "MC-E2",
        lane: "E",
        method: "POST",
        path: "/staff/conversations/:conversationId/join",
        routeClass: "staff",
        // A supervisor, or a concierge with an effective assignment (any kind) to the customer. Checked in the transaction.
        authorization: "concierge|supervisor",
        request: null,
        response: "{ channel: { type: 'messaging', id } }",
        success: 200,
        idempotencyKey: "none",
        existing: false,
        errors: [...STAFF_ERRORS, "404 not_found"],
    },
    staffCopilotSession: {
        id: "MC-E3",
        lane: "E",
        method: "GET",
        path: "/staff/conversations/:conversationId/copilot/session",
        routeClass: "staff",
        authorization: "concierge|supervisor",
        request: null,
        response: "staffCopilotSessionSchema",
        success: 200,
        idempotencyKey: "none",
        existing: false,
        errors: [...STAFF_ERRORS, "404 not_found"],
    },
    staffCopilotRun: {
        id: "MC-E4",
        lane: "E",
        method: "POST",
        path: "/staff/conversations/:conversationId/copilot/runs",
        routeClass: "staff",
        authorization: "concierge|supervisor",
        request: "staffCopilotRunBodySchema",
        response: "AI SDK UI message stream (SSE), ends with run-status",
        success: 200,
        idempotencyKey: "required",
        existing: false,
        errors: [
            ...STAFF_ERRORS,
            "404 not_found",
            "409 agent_run_in_progress",
            "503 agent_unavailable",
        ],
    },
    // --- lane D1: trips, saved ----------------------------------------------------------------------------------------------
    customerTrips: {
        id: "MC-D1-1",
        lane: "D1",
        method: "GET",
        path: "/customer/trips?view=&cursor=&limit=",
        routeClass: "customer",
        authorization: "customer",
        request: "customerTripsQuerySchema",
        response: "customerTripsPageSchema",
        success: 200,
        idempotencyKey: "none",
        existing: false,
        errors: [...CUSTOMER_ERRORS, "400 invalid_request"],
    },
    customerTripDetail: {
        id: "MC-D1-2",
        lane: "D1",
        method: "GET",
        path: "/customer/trips/:tripId",
        routeClass: "customer",
        authorization: "customer",
        request: null,
        response: "customerTripDetailSchema",
        success: 200,
        idempotencyKey: "none",
        existing: false,
        errors: [...CUSTOMER_ERRORS, "404 not_found (also another customer's trip)"],
    },
    customerSavedList: {
        id: "MC-D1-3",
        lane: "D1",
        method: "GET",
        path: "/customer/saved?targetType=&cursor=&limit=",
        routeClass: "customer",
        authorization: "customer",
        request: "customerSavedQuerySchema",
        response: "customerSavedPageSchema",
        success: 200,
        idempotencyKey: "none",
        existing: false,
        errors: [...CUSTOMER_ERRORS, "400 invalid_request"],
    },
    customerSave: {
        id: "MC-D1-4",
        lane: "D1",
        method: "POST",
        path: "/customer/saved",
        routeClass: "customer",
        authorization: "customer",
        request: "customerSaveBodySchema",
        response: "customerSavedItemSchema",
        success: 200,
        idempotencyKey: "required",
        existing: false,
        errors: [...CUSTOMER_ERRORS, "404 not_found", "409 conflict (saved_item.target_unavailable)"],
    },
    customerUnsave: {
        id: "MC-D1-5",
        lane: "D1",
        method: "POST",
        path: "/customer/saved/:savedItemId/remove",
        routeClass: "customer",
        authorization: "customer",
        request: null,
        response: "customerSavedItemSchema",
        success: 200,
        idempotencyKey: "required",
        existing: false,
        errors: [...CUSTOMER_ERRORS, "404 not_found"],
    },
    // CHT-736 plan item 6 (added by lane D1 with the primary agent's approval): trip create, edit and cancel.
    customerTripCreate: {
        id: "MC-D1-6",
        lane: "D1",
        method: "POST",
        path: "/customer/trips",
        routeClass: "customer",
        authorization: "customer",
        request: "customerTripCreateBodySchema",
        response: "customerTripCommandOutcomeSchema",
        success: 201,
        idempotencyKey: "required",
        existing: false,
        errors: [...CUSTOMER_ERRORS, "400 invalid_request", "422 unprocessable (tripRefusal.field)"],
    },
    customerTripEdit: {
        id: "MC-D1-7",
        lane: "D1",
        method: "POST",
        path: "/customer/trips/:tripId/edit",
        routeClass: "customer",
        authorization: "customer",
        request: "customerTripEditBodySchema",
        response: "customerTripCommandOutcomeSchema",
        success: 200,
        idempotencyKey: "required",
        existing: false,
        errors: [
            ...CUSTOMER_ERRORS,
            "400 invalid_request",
            "404 not_found (also another customer's trip)",
            "409 conflict (tripRefusal: trip.version_conflict, trip.not_editable, trip.terminal)",
            "422 unprocessable (tripRefusal: trip.plan_incomplete, trip.invalid with field)",
        ],
    },
    customerTripCancel: {
        id: "MC-D1-8",
        lane: "D1",
        method: "POST",
        path: "/customer/trips/:tripId/cancel",
        routeClass: "customer",
        authorization: "customer",
        request: "customerTripCancelBodySchema",
        response: "customerTripCommandOutcomeSchema",
        success: 200,
        idempotencyKey: "required",
        existing: false,
        errors: [
            ...CUSTOMER_ERRORS,
            "400 invalid_request",
            "404 not_found (also another customer's trip)",
            "409 conflict (tripRefusal: trip.version_conflict, trip.terminal, trip.transition_invalid)",
        ],
    },
    // CHT-765 (lane TC): the two trip transitions a customer needs to make a plan UPCOMING (and so reach the proactive sweeps).
    customerTripConfirm: {
        id: "MC-D1-10",
        lane: "D1",
        method: "POST",
        path: "/customer/trips/:tripId/confirm",
        routeClass: "customer",
        authorization: "customer",
        request: "customerTripConfirmBodySchema",
        response: "customerTripCommandOutcomeSchema",
        success: 200,
        idempotencyKey: "required",
        existing: false,
        errors: [
            ...CUSTOMER_ERRORS,
            "400 invalid_request",
            "404 not_found (also another customer's trip)",
            "409 conflict (tripRefusal: trip.version_conflict, trip.terminal, trip.transition_invalid)",
            "422 unprocessable (tripRefusal: trip.plan_incomplete)",
        ],
    },
    customerTripReopen: {
        id: "MC-D1-11",
        lane: "D1",
        method: "POST",
        path: "/customer/trips/:tripId/reopen",
        routeClass: "customer",
        authorization: "customer",
        request: "customerTripReopenBodySchema",
        response: "customerTripCommandOutcomeSchema",
        success: 200,
        idempotencyKey: "required",
        existing: false,
        errors: [
            ...CUSTOMER_ERRORS,
            "400 invalid_request",
            "404 not_found (also another customer's trip)",
            "409 conflict (tripRefusal: trip.version_conflict, trip.terminal, trip.transition_invalid)",
        ],
    },
    // CHT-737 (added by lane D1 with the primary agent's approval): one save by id, for the deep link `/saved/[savedItemId]`.
    customerSavedGet: {
        id: "MC-D1-9",
        lane: "D1",
        method: "GET",
        path: "/customer/saved/:savedItemId",
        routeClass: "customer",
        authorization: "customer",
        request: null,
        response: "customerSavedItemSchema",
        success: 200,
        idempotencyKey: "none",
        existing: false,
        errors: [
            ...CUSTOMER_ERRORS,
            "404 not_found (also another customer's save and a save that was removed)",
        ],
    },
    // --- lane D2: action center, profile ------------------------------------------------------------------------------------
    customerActions: {
        id: "MC-D2-1",
        lane: "D2",
        method: "GET",
        path: "/customer/actions?view=&bucket=&cursor=&limit=",
        routeClass: "customer",
        authorization: "customer",
        request: "customerActionsQuerySchema",
        response: "customerActionsPageSchema",
        success: 200,
        idempotencyKey: "none",
        existing: false,
        errors: [...CUSTOMER_ERRORS, "400 invalid_request"],
    },
    customerActionDetail: {
        id: "MC-D2-2",
        lane: "D2",
        method: "GET",
        path: "/customer/actions/:actionId",
        routeClass: "customer",
        authorization: "customer",
        request: null,
        response: "customerActionItemV2Schema",
        success: 200,
        idempotencyKey: "none",
        existing: false,
        errors: [...CUSTOMER_ERRORS, "404 not_found (also another customer's action, or an ended one)"],
    },
    customerProfile: {
        id: "MC-D2-3",
        lane: "D2",
        method: "GET",
        path: "/customer/profile",
        routeClass: "customer",
        authorization: "customer",
        request: null,
        response: "customerProfileViewSchema",
        success: 200,
        idempotencyKey: "none",
        existing: false,
        errors: [...CUSTOMER_ERRORS],
    },
    customerProfileUpdate: {
        id: "MC-D2-4",
        lane: "D2",
        method: "POST",
        path: "/customer/profile",
        routeClass: "customer",
        authorization: "customer",
        request: "customerProfileUpdateBodySchema",
        response: "customerProfileViewSchema",
        success: 200,
        idempotencyKey: "required",
        existing: false,
        errors: [...CUSTOMER_ERRORS, "409 conflict (profile.version_conflict)", "422 unprocessable"],
    },
    customerNotificationPreferences: {
        id: "MC-D2-5",
        lane: "D2",
        method: "GET",
        path: "/customer/notification-preferences",
        routeClass: "customer",
        authorization: "customer",
        request: null,
        response: "customerNotificationPreferencesSchema",
        success: 200,
        idempotencyKey: "none",
        existing: false,
        errors: [...CUSTOMER_ERRORS],
    },
    customerNotificationPreferencesUpdate: {
        id: "MC-D2-6",
        lane: "D2",
        method: "PUT",
        path: "/customer/notification-preferences",
        routeClass: "customer",
        authorization: "customer",
        request: "customerNotificationPreferencesBodySchema",
        response: "customerNotificationPreferencesSchema",
        success: 200,
        idempotencyKey: "required",
        existing: false,
        errors: [...CUSTOMER_ERRORS, "409 conflict (notification_preferences.version_conflict)"],
    },
    // Added by lane D2 with the approval of the primary agent (CHT-738 review): the customer answers the question of a request in
    // AWAITING_CUSTOMER from an Action Center screen. It calls the existing `respondToConciergeRequest` use case.
    customerRequestRespond: {
        id: "MC-D2-7",
        lane: "D2",
        method: "POST",
        path: "/customer/concierge-requests/:requestId/respond",
        routeClass: "customer",
        authorization: "customer",
        request: "customerRequestRespondBodySchema",
        response: "customerConciergeRequestViewSchema",
        success: 200,
        idempotencyKey: "required",
        existing: false,
        errors: [
            ...CUSTOMER_ERRORS,
            "400 invalid_request",
            "404 not_found (also another customer's request)",
            "409 conflict (the request no longer waits for an answer)",
        ],
    },
});
